/**
 * 获取tea公共参数相关工具函数
 * 主要分两类，一类是同步的，一类是异步的
 * 同步的参数获取完后即可开始TEA上报，异步的获取到数据后再更新上去
 * 主要考虑到用户跳出问题，等待太久再初始化上报可能会丢PV上报等数据
 * PRD https://bytedance.feishu.cn/docs/doccns1mTnYUdjncA7aLwCV8Znc
 */
import { Context } from "@nuxt/types";
import Cookies from "js-cookie";
import { Route } from "vue-router";
import Tea from "@dp/byted-tea-sdk-tcpy";
import { EventOrParam } from '@ad-i18n/getstarted-sdk';
import { extendReportContext } from './report';
import TYPES from "~/store/types";
import { ADV_INFO } from "~/types/account";
import { DEFAULT_LANG } from "~/const/starling";

type ExtendParamCallback = (conf: Record<string, string>) => void;

/**
 * 所有要获取的Tea公共参数信息
 * 每项都是数组，数组第一项为获取函数，后面均为要设置的参数名，可以有多个
 * 新增参数获取则按照规范实现后，在下方数组添加即可
 */
const paramTaskInfoArray = [
  [getTraceId, "trace_sid"],
  [getUserStatus, "user_status"],
  [getUserId, "tt4b_user_id"],
  [getAdvids, "adv_ids"],
  [getLang, "app_language"],
  [getABVersion, "ab_version", "ab_sdk_version"],
  [getRefer, "refer"],
  [getUid, "user_id", "user_unique_id"],
  [getCurrentUrl, "current_url"],
  [getCountry, "country"],
  [getCity, "city"],
  [getPlatform, "from", "platform"],
  [getIsInIframe, "is_in_iframe"],
  [getUserType, 'user_type_by_url']
];

/**
 * 拓展Tea公共参数
 * @param ctx nuxt上下文
 * @param
 */
export function executeParamsHandlers(ctx: Context) {
  // 每个任务处理完后要执行的回调
  // 将得到的公共参数，配置到Tea & Slardar上，并通知父页面同步设置
  const callback: ExtendParamCallback = params => extendReportContext(params, ctx)

  // 要并行执行的获取参数的任务
  const paramTasks = paramTaskInfoArray.map(info =>
    extendSingleTeaParams(ctx, callback, info)
  );

  // 并行处理所有要拓展的参数，此处不需要await，无需等待所有参数获取完成，不阻塞外部开始Tea上报
  Promise.all(paramTasks).then(params => {
    console.info("[Tea]: get tea headerParams", params);
  });
}

async function extendSingleTeaParams(
  ctx: Context,
  cb: ExtendParamCallback,
  info: typeof paramTaskInfoArray[number]
) {
  const [task, ...keys] = info;
  if (typeof task !== "function" || !keys?.length) return {};
  const value = await task?.(ctx).catch(() => "");

  const config: Record<string, string> = keys.reduce((acc, key) => {
    if (typeof key !== "string") return {};

    // 将获取到的参数更新到外部传入的对象上，同时内部拼接一个局部config
    return {
      ...acc,
      [key as string]: value
    };
  }, {});

  // 将要拓展的配置传递给外部callback
  cb?.(config);
  return config;
}

/** 获取归因SDK生成的追踪ID */
export function getTraceId(ctx: Context): Promise<string> {
  return Promise.resolve(Cookies.get('tta_attr_id_mirror') || '');
}

/** 从归因trace_sid上获取最后一段作为tea上报的unique_id */
export function getUid(ctx: Context): Promise<string> {
  // 有 advid 用它当 user id，用于支持 ttam coupon 活动
  const aadvid = ctx?.query?.aadvid || ''
  const traceSid = ctx?.store.state.ttaId || "";
  return Promise.resolve( aadvid || traceSid.split(".")?.pop() || "");
}

/** 用户User登录状态，login=有User登录态，logout=无User登录态 */
export type UserStatus = "login" | "logout";

/** 获取用户User登录状态 */
export function getUserStatus(ctx: Context): Promise<UserStatus> {
  return new Promise<UserStatus>(resolve => {
    const stop = ctx.store.subscribe((mutation, state) => {
      if (mutation.type === TYPES.SET_LOGIN) {
        resolve(mutation.payload ? "login" : "logout");
        stop();
      }
    });
  });
}

/** 获取用户userId */
export function getUserId(ctx: Context): Promise<string> {
  return new Promise<string>(resolve => {
    const stop = ctx.store.subscribe((mutation, state) => {
      if (mutation.type === TYPES.SET_LOGIN_INFO) {
        resolve(mutation.payload?.id || "");
        stop();
      }
    });
  });
}

/** 获取用户user下的所有advid */
export function getAdvids(ctx: Context): Promise<string> {
  return new Promise<string>(resolve => {
    const stop = ctx.store.subscribe((mutation, state) => {
      if (mutation.type === TYPES.SET_ADV_LIST) {
        const list: ADV_INFO[] = mutation.payload || [];
        const idsString = list
          .map(item => item?.id)
          .filter(Boolean)
          .join(",");
        resolve(idsString);
        stop();
      }
    });
  });
}

/** 获取当前语言 */
export function getLang(ctx: Context): Promise<string> {
  return Promise.resolve(ctx?.store.state.lang || DEFAULT_LANG);
}

/** 获取当前libra ab实验 version */
export function getABVersion(ctx: Context): Promise<string> {
  const abVersion =
    ctx?.store.state.ABTestConfig?.version_name || Cookies.get("vid") || "";
  return Promise.resolve(abVersion);
}

/** 获取URL query上的refer */
export function getRefer(ctx: Context): Promise<string> {
  const refer = String(ctx?.route?.query?.refer || "");

  ctx.store.commit(TYPES.SET_QUERY_REFER, refer);
  return Promise.resolve(refer);
}

/** 获取当前URL */
export function getCurrentUrl(_ctx: Context): Promise<string> {
  return Promise.resolve(window.location.href);
}

/** 获取用户国家码 */
export function getCountry(ctx: Context): Promise<string> {
  const country = ctx.store.state.user?.userLocation?.Country?.Code || "";
  return Promise.resolve(country);
}

/** 获取用户城市名 */
export function getCity(ctx: Context): Promise<string> {
  const country = ctx.store.state.user?.userLocation?.City?.ASCIName || "";
  return Promise.resolve(country);
}

/** 获取链接上的Platform */
export function getPlatform(ctx: Context): Promise<string> {
  // 优先使用父页面query优先级
  return ctx.$handshake?.handshakePromise?.then(parent => {
    const searchParamsFromParent = parent?.model?.[EventOrParam.searchParamsFromParent]
    return String(searchParamsFromParent?.platform) || ''
  })
    // 其次使用本页面query
    || Promise.resolve(String(ctx?.route.query.platform) || "");
}

/** 获取当前页面是否被iframe加载 */
export function getIsInIframe(ctx: Context): Promise<string> {
  const isInIframe = ctx.$displayFormInIframe === true ? '1' : '0'
  // 优先使用父页面query优先级
  return Promise.resolve(isInIframe)
}

export function getUserType(ctx: Context) {
  let user_type_by_url = 'anonymous' // 'anonymous' | 'ttam'
  const aadvid = ctx?.route.query.aadvid
  user_type_by_url = aadvid ? 'ttam' : user_type_by_url
  return Promise.resolve(user_type_by_url)
}

/** 获取通用的Tea上报事件参数 */
export function getCommonEventParams(route?: Route) {
  const { name = "", query = {} } = route || {};
  const {
    refer = "",
    channel = undefined,
    platform = undefined,
    utm_campaign = undefined
  } = query;

  return {
    url: window.location.href,
    refer,
    page_type: name,
    page_channel: channel,
    page_platform: platform,
    page_campaign: utm_campaign
  };
}

/**
 * 拓展tea公共上下文属性
 * @see https://bytedance.feishu.cn/wiki/wikcnUGmq3tK9Snq3xCVWqovfQg#
 */
 export function extendTeaCommonParams(params: Record<string, string> = {}) {
  // 将对应的key & value配置到tea上
  Tea.config(params);
}
